import React, { useState } from "react";
import { X, Save, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

import { Section, TextField, IconBtn } from "@/pages/admin/fields/primitives";

/**
 * HardwareClassEditorDrawer — Workstations Final System Pass (Feb 2026)
 *
 * Edits a single `hardware_classes` document via
 * PUT /api/admin/hardware-classes/{slug}. Governs the pricing floor,
 * platform-specific pricing, current-shipping model and optional add-ons
 * for every use case that maps to this hardware class.
 *
 * min_spec is editable but carries a warning banner — do not lower it
 * below the approved workload requirement.
 *
 * Add-on ID is read-only (compatibility with existing cart / order data).
 */
const newAddon = () => ({
  id: `hcaddon_${Math.random().toString(36).slice(2, 10)}`,
  name: "",
  description: "",
  price: 0,
});

export const HardwareClassEditorDrawer = ({ item, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({
    slug: "",
    display_name: "",
    description: "",
    min_spec: [],
    windows: { active: true, price: 0, is_placeholder: true, current_model: "", badge: "", subtext: "" },
    mac: { active: false, price: 0, is_placeholder: true, current_model: "", badge: "", subtext: "" },
    addons: [],
    active: true,
    display_order: 0,
    ...(item || {}),
  }));

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    const numeric = k === "display_order";
    setForm((f) => ({ ...f, [k]: numeric ? Number(v || 0) : v }));
  };
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  const setPlatform = (which, k) => (e) => {
    const raw = e?.target ? e.target.value : e;
    const isNum = k === "price";
    const isBool = k === "active" || k === "is_placeholder";
    const val = isNum ? Number(raw || 0) : (isBool ? !!raw : raw);
    setForm((f) => ({ ...f, [which]: { ...f[which], [k]: val } }));
  };
  const togglePlatform = (which, k) => () =>
    setForm((f) => ({ ...f, [which]: { ...f[which], [k]: !f[which][k] } }));

  // ---- min_spec ----
  const addSpec = () => setForm((f) => ({ ...f, min_spec: [...(f.min_spec || []), ""] }));
  const removeSpec = (i) => setForm((f) => ({ ...f, min_spec: f.min_spec.filter((_, j) => j !== i) }));
  const setSpec = (i, v) => setForm((f) => ({
    ...f,
    min_spec: f.min_spec.map((x, j) => (j === i ? v : x)),
  }));

  // ---- addons ----
  const addAddon = () => setForm((f) => ({ ...f, addons: [...(f.addons || []), newAddon()] }));
  const removeAddon = (i) => setForm((f) => ({ ...f, addons: f.addons.filter((_, j) => j !== i) }));
  const setAddon = (i, patch) => setForm((f) => ({
    ...f,
    addons: f.addons.map((x, j) => (j === i ? { ...x, ...patch } : x)),
  }));

  const commit = () => {
    if (!form.display_name?.trim()) {
      toast.error("Display Name is required.");
      return;
    }
    onSave(form);
  };

  const platformBlock = (which, title) => (
    <div className="rounded-xl border border-[#262626] bg-[#0A0A0A] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-[15px]">{title}</div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form[which]?.active}
            onChange={togglePlatform(which, "active")}
            className="accent-[#FF6600]"
            data-testid={`hc-${which}-active`}
          />
          <span className="text-[11px] uppercase tracking-widest text-[#A3A3A3]">Active</span>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Price (AWG)</label>
          <input
            type="number"
            value={form[which]?.price ?? 0}
            onChange={setPlatform(which, "price")}
            data-testid={`hc-${which}-price`}
            className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
          />
        </div>
        <label className="flex items-end gap-2 pb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form[which]?.is_placeholder}
            onChange={togglePlatform(which, "is_placeholder")}
            className="accent-[#FF6600]"
            data-testid={`hc-${which}-placeholder`}
          />
          <span className="text-[11px] uppercase tracking-widest text-[#A3A3A3] leading-none">
            Placeholder Pricing<br />
            <span className="normal-case tracking-normal text-[10px]">Shows "Pricing under review" on the site.</span>
          </span>
        </label>
      </div>
      <TextField
        label="Current Model"
        value={form[which]?.current_model || ""}
        onChange={setPlatform(which, "current_model")}
        placeholder="e.g. HP EliteBook 840 G11 · Intel Core Ultra 7 155H · 16 GB · 1 TB"
        hint="What Ur Designs is currently shipping. Must meet or exceed the minimum specification."
      />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Badge (optional)" value={form[which]?.badge || ""} onChange={setPlatform(which, "badge")} placeholder="e.g. Recommended" />
        <TextField label="Subtext (optional)" value={form[which]?.subtext || ""} onChange={setPlatform(which, "subtext")} placeholder="Extra note when platform is disabled or promoted." />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-end" onClick={onClose}>
      <aside
        data-testid="hardware-class-editor-drawer"
        className="bg-[#0F0F0F] w-full max-w-2xl h-full border-l border-[#262626] p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6 sticky top-0 -mt-6 -mx-6 px-6 py-4 bg-[#0F0F0F] border-b border-[#262626] z-10">
          <div>
            <div className="ur-eyebrow">Hardware Class</div>
            <h3 className="font-display text-xl font-bold mt-1">
              Edit · {form.display_name || form.slug}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="hardware-class-save"
              onClick={commit}
              className="ur-btn-primary text-sm py-2.5 px-4"
            >
              Save <Save className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Basics */}
        <Section title="Basics" hint="Internal hardware class label. Customers never see the class name — they see the use-case name.">
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Slug (read-only)" value={form.slug} onChange={() => {}} />
            <TextField label="Display Name (internal)" value={form.display_name} onChange={set("display_name")} placeholder="e.g. Everyday Workstation" />
          </div>
          <TextField
            label="Description (internal note)"
            textarea
            rows={2}
            value={form.description}
            onChange={set("description")}
          />
        </Section>

        {/* Min spec */}
        <Section
          title="Minimum Specifications"
          hint={"Minimum specifications define the guaranteed hardware floor for this workstation class. Do not lower them below the approved workload requirement."}
        >
          <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3 text-[12px] text-[#F59E0B] leading-relaxed">
            ⚠ Minimum specifications define the guaranteed hardware floor for this workstation class.
            Do not lower them below the approved workload requirement.
          </div>
          <div className="space-y-2" data-testid="hc-min-spec-editor">
            {(form.min_spec || []).map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={line}
                  onChange={(e) => setSpec(i, e.target.value)}
                  className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
                <IconBtn onClick={() => removeSpec(i)} danger><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addSpec}
            data-testid="hc-add-spec"
            className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF6600] font-semibold"
          >
            <Plus className="w-4 h-4" /> Add Specification
          </button>
        </Section>

        {/* Platform pricing */}
        <Section title="Platform Pricing" hint="Prices are AWG. Server remains authoritative — this is the single source of truth.">
          <div className="grid gap-4">
            {platformBlock("windows", "Windows")}
            {platformBlock("mac", "Mac")}
          </div>
        </Section>

        {/* Add-ons */}
        <Section title="Optional Add-ons" hint="Rendered as the 'Customize Your Workstation' rows on the product page. IDs are locked for cart/order compatibility.">
          <div className="space-y-3" data-testid="hc-addons-editor">
            {(form.addons || []).map((a, i) => (
              <div key={a.id} className="rounded-xl border border-[#262626] bg-[#141414] p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Add-on Name"
                    value={a.name || ""}
                    onChange={(e) => setAddon(i, { name: e.target.value })}
                    className="flex-1 bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                  />
                  <IconBtn onClick={() => removeAddon(i)} danger><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={a.description || ""}
                  onChange={(e) => setAddon(i, { description: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Price (AWG)</label>
                    <input
                      type="number"
                      value={a.price ?? 0}
                      onChange={(e) => setAddon(i, { price: Number(e.target.value || 0) })}
                      className="mt-1 w-full bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">ID (locked)</label>
                    <div className="mt-1 w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg px-3 py-2 text-[11px] text-[#7A7A7A] font-mono">
                      {a.id}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addAddon}
            data-testid="hc-add-addon"
            className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF6600] font-semibold"
          >
            <Plus className="w-4 h-4" /> Add Add-on
          </button>
        </Section>

        {/* Visibility */}
        <Section title="Visibility">
          <label className="flex items-center gap-3 rounded-xl border border-[#262626] bg-[#141414] p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.active}
              onChange={toggle("active")}
              className="accent-[#FF6600]"
              data-testid="hc-toggle-active"
            />
            <div>
              <div className="text-sm font-semibold">Active</div>
              <div className="text-[11px] text-[#A3A3A3]">Inactive hardware classes are hidden from resolvers.</div>
            </div>
          </label>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Display Order</label>
            <input
              type="number"
              value={form.display_order}
              onChange={set("display_order")}
              className="mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
            />
          </div>
        </Section>
      </aside>
    </div>
  );
};
